<template>
  <v-container class="forecasting-prediction-container px-6 pt-0 pb-3">
    <v-row
      class="forecasting-prediction-loading ma-0 pa-0"
      align="center"
      justify="center"
      v-if="data_loading"
    >
      <v-col cols="auto">
        <v-progress-circular
          indeterminate
          color="primary"
          size="100"
        ></v-progress-circular>
      </v-col>
    </v-row>
    <v-row class="ma-0 pa-0 forecasting_prediction_layout" v-if="!data_loading">
      <v-col cols="12" md="1" class="ma-0 pa-0 pt-3 pr-3 forecasting_prediction_tabs">
        <span class="forecasting_prediction_rail_label">Optimizations</span>
        <button
          class="forecasting_prediction_tab"
          :class="{
            'forecasting_prediction_tab_active':
              selected_optimization_tab_key === 'overview',
          }"
          type="button"
          @click="select_overview_tab"
        >
          Overview
        </button>
        <button
          v-for="(opt, index) in optimizations"
          class="forecasting_prediction_tab"
          :class="{
            'forecasting_prediction_tab_active':
              selected_optimization_tab_key === get_optimization_tab_key(opt, index),
          }"
          :key="get_optimization_tab_key(opt, index)"
          type="button"
          @click="select_optimization_tab(opt, index)"
        >
          {{ opt }}
        </button>
      </v-col>
      <v-col cols="auto" class="ma-0 pa-0 forecasting_prediction_divider">
        <v-divider vertical></v-divider>
      </v-col>
      <v-col class="ma-0 pa-0 pl-4 forecasting_prediction_content">
        <v-row
          class="ma-0 pa-0 forecasting_prediction_detail"
          v-if="my_optimization !== 'overview'"
        >
          <v-col cols="12" md="8" class="px-1 forecasting_prediction_primary">
            <v-row class="ma-0 pa-0">
              <v-col cols="4" class="ma-0 pa-0">
                <PredictionStatistic
                  statistic_title_name="Spend"
                  :value="optimization_data[my_optimization]['spend']"
                  :change="optimization_data[my_optimization]['spend_change']"
                ></PredictionStatistic>
              </v-col>
              <v-col cols="4" class="ma-0 pa-0">
                <PredictionStatistic
                  statistic_title_name="Volume"
                  :value="optimization_data[my_optimization]['volume']"
                  :change="optimization_data[my_optimization]['volume_change']"
                ></PredictionStatistic>
              </v-col>
              <v-col cols="4" class="ma-0 pa-0">
                <PredictionStatistic
                  statistic_title_name="ROAS"
                  :value="optimization_data[my_optimization]['roas']"
                  :change="optimization_data[my_optimization]['roas_change']"
                ></PredictionStatistic>
              </v-col>
            </v-row>
            <DataLevelTable
              :tableHeader="optimization_data[my_optimization]['tbl_header']"
              :tableData="optimization_data[my_optimization]['tbl_data']"
              :selectedLevels="['channel']"
              :tableRowClickFunction="empty_table_click"
              :numericColumns="numeric_cols"
              :currencyColumns="currency_cols"
              :percentageColumns="change_cols"
              :decimalColumns="[]"
              :mid="true"
              :color="true"
              :color-map="prediction_channel_color_map"
            />
          </v-col>
          <v-col cols="12" md="4" class="forecasting_prediction_pie_column">
            <v-row class="ma-0 pa-0 forecasting_prediction_pie_stack">
              <v-col cols="12" class="ma-0 pa-0">
                <PredictionPieChart
                  :value="prediction_spend_pie_data"
                  statistic_title_name="Spend"
                  ref="prediction_spend_chart_component"
                  :chart_id="chart_mapping['prediction_spend_chart']['chart_id']"
                  :chart_ref="chart_mapping['prediction_spend_chart']['chart_ref']"
                  :colors="channel_colors"
                  :channel-colors="prediction_channel_color_map"
                ></PredictionPieChart>
              </v-col>
              <v-col cols="12" class="ma-0 pa-0">
                <PredictionPieChart
                  :value="prediction_volume_pie_data"
                  statistic_title_name="Volume"
                  ref="prediction_volume_chart_component"
                  :chart_id="chart_mapping['prediction_volume_chart']['chart_id']"
                  :chart_ref="chart_mapping['prediction_volume_chart']['chart_ref']"
                  :colors="channel_colors"
                  :channel-colors="prediction_channel_color_map"
                ></PredictionPieChart>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            v-if="show_blocking_chart"
            cols="12"
            class="pa-0 forecasting_prediction_blocking_chart"
          >
            <ForecastingOptimizationBlockingChart
              :project_date_range="project_date_range"
              :data_levels="data_levels"
              :data_filters="data_filters"
              :optimization_table_data="optimization_data[my_optimization]['tbl_data']"
              v-on:triggerAlert="forward_alert"
            ></ForecastingOptimizationBlockingChart>
          </v-col>
        </v-row>
        <template v-if="my_optimization === 'overview'">
          <v-row class="ma-0 pa-0 forecasting_prediction_overview">
            <v-col cols="12" class="py-0 pt-3">
              <PredictionBarChartOverview
                :value="prediction_overview_data"
                ref="prediction_overview_chart_component"
                :chart_ref="chart_mapping['overview_chart']['chart_ref']"
                :chart_id="chart_mapping['overview_chart']['chart_id']"
              ></PredictionBarChartOverview>
            </v-col>
            <v-col cols="12" class="py-0">
              <DataLevelTable
                :tableHeader="optimization_header"
                :tableData="opt_data"
                :selectedLevels="['opt']"
                :tableRowClickFunction="handle_row_click"
                :numericColumns="numeric_cols"
                :currencyColumns="currency_cols"
                :percentageColumns="change_cols"
                :decimalColumns="[]"
                :short="true"
                :color="true"
                :not_sorted="true"
                :filterable="false"
              />
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE } from "@/constants/constants";
import predictionConfig from "@/configs/Planning/prediction_line_chart.json";
import PredictionPieChart from "./Charts/PredictionPieChart.vue";
import PredictionBarChartOverview from "./Charts/PredictionBarChartOverview.vue";
import ForecastingOptimizationBlockingChart from "./ForecastingOptimizationBlockingChart.vue";

import PredictionExample from "@/test_data/planning/planning_example_a.json";

import DataLevelTable from "@/components/Utility/DataLevelTable.vue";

import PredictionStatistic from "./Charts/PredictionStatistic.vue";
import { DELAY, EXPORT_DELAY_IN_MS } from "@/constants/constants.js";
import PPTXBuilderPrediction from "@/javascript/export_builders/pptxBuilder_planning_prediction.js";
import * as echarts from 'echarts';
import api_caller from "@/javascript/optimization.js";
import {
  FORECASTING_CHANNEL_COLORS,
  buildForecastingChannelColorMap,
} from "./forecastingChannelColors";

const STAT_ELEMENTS = [
  {
    name: "Spend",
    value: "spend",
    change: "spend_change",
  },
  {
    name: "Volume",
    value: "volume",
    change: "volume_change",
  },
  {
    name: "ROAS",
    value: "roas",
    change: "roas_change",
  },
]

const CHART_MAPPING = {
  overview_chart: {
    chart_ref: "overview_chart",
    chart_id: "overview_chart",
    chart_name: "Prediction Overview Chart",
  },
  prediction_spend_chart: {
    chart_ref: "prediction_spend_chart",
    chart_id: "prediction_spend_chart",
    chart_name: "Prediction Spend Chart",
  },
  prediction_volume_chart: {
    chart_ref: "prediction_volume_chart",
    chart_id: "prediction_volume_chart",
    chart_name: "Prediction Volume Chart",
  },
};

export default {
  name: "ForecastingPrediction",
  props: {
    project_plan_id: {
      type: [String, Number],
      required: true,
    },
    saved: {
      type: Boolean,
      default: false,
    },
    data_version: {
      type: String,
      default: null,
    },
    version_tag: {
      type: String,
      default: null,
    },
    model_version: {
      type: String,
      default: "v1",
    },
    project_date_range: {
      type: Object,
      default: () => ({}),
    },
    data_levels: {
      type: Object,
      default: () => ({}),
    },
    data_filters: {
      type: Object,
      default: () => ({}),
    },
    show_blocking_chart: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    PredictionPieChart,
    DataLevelTable,
    PredictionBarChartOverview,
    PredictionStatistic,
    ForecastingOptimizationBlockingChart,
  },
  data: () => ({
    data_loading: true,
    chart_mapping: CHART_MAPPING,
    optimization_settings_menu: false,
    optimization_settings: {
      display: ["base", "low", "mid", "high"],
    },

    prediction_chart_config: predictionConfig,

    data_raw: null,
    available_level_vals: ["Media", "OOH", "Programmatic", "Social", "Search"],
    selected_level_val: "Media",
    data: PredictionExample["overview"]["Media"]["daily"],
    attribution: PredictionExample["overview"]["Media"]["Attribution"],
    change_last: PredictionExample["overview"]["Media"]["Change_Last"],
    change_prev: PredictionExample["overview"]["Media"]["Change_Prev"],

    roas_compare: PredictionExample["roas"],
    contribution_compare: PredictionExample["contribution"],

    optimizations: [],
    optimization_data: {},
    my_optimization: "overview",
    selected_optimization_tab_key: "overview",

    headers: [],
    opt_data: [],
    opt_overview_data: {},
    base_pie_data: null,
    low_pie_data: null,
    mid_pie_data: null,
    high_pie_data: null,

    prediction_overview_data: null,
    channel_colors: FORECASTING_CHANNEL_COLORS.slice(),
    prediction_metric_options: [
      { label: "Spend", value: "spend" },
      { label: "Volume", value: "volume" },
    ],
  }),
  mounted() {
    this.data_raw = PredictionExample;
    this.buildPrediction();
  },
  watch: {
    project_plan_id: function () {
      this.select_overview_tab();
      this.buildPrediction();
    },
    model_version: function () {
      this.select_overview_tab();
      this.buildPrediction();
    },
  },
  computed: {
    numeric_cols() {
      return [];
    },
    currency_cols() {
      let cols = ["spend", "volume", "roas"];
      return cols;
    },
    change_cols() {
      let change_cols = ["spend_change", "volume_change", "roas_change"];
      return change_cols;
    },
    optimization_header: function () {
      let new_header = [];
      for (let header of this.headers) {
        new_header.push(header);
      }
      return new_header;
    },
    prediction_channel_color_map: function () {
      if (
        this.my_optimization === "overview" ||
        !this.optimization_data[this.my_optimization] ||
        !this.optimization_data[this.my_optimization]["tbl_data"]
      ) {
        return {};
      }

      const channels = this.optimization_data[this.my_optimization]["tbl_data"].map(
        (row) => row.channel
      );

      return buildForecastingChannelColorMap(channels);
    },
    prediction_spend_pie_data: function () {
      return this.get_prediction_pie_data("spend");
    },
    prediction_volume_pie_data: function () {
      return this.get_prediction_pie_data("volume");
    },
  },
  methods: {
    update_data_loading: function (isLoading) {
      this.data_loading = isLoading;
      this.$emit("prediction_loading_update", isLoading);
    },
    get_prediction_pie_data: function (metric) {
      if (
        this.my_optimization === "overview" ||
        !this.optimization_data[this.my_optimization]
      ) {
        return [];
      }

      const optimization = this.optimization_data[this.my_optimization];
      const existingPieData = this.get_existing_metric_pie_data(
        optimization,
        metric
      );

      if (existingPieData) {
        return existingPieData;
      }

      return this.build_metric_pie_data(
        optimization["tbl_data"],
        metric
      );
    },
    get_existing_metric_pie_data: function (optimization, metric) {
      const metricPieKeys = {
        spend: ["pie_data", "spend_pie_data", "pie_data_spend"],
        volume: ["volume_pie_data", "pie_data_volume"],
      };
      const keys = metricPieKeys[metric] || [];

      for (let key of keys) {
        if (Array.isArray(optimization[key])) {
          return optimization[key];
        }
      }

      if (
        optimization["pie_data_by_metric"] &&
        Array.isArray(optimization["pie_data_by_metric"][metric])
      ) {
        return optimization["pie_data_by_metric"][metric];
      }

      return null;
    },
    build_metric_pie_data: function (tableData, metric) {
      const metricConfig = this.prediction_metric_options.find(
        (option) => option.value === metric
      );

      if (!metricConfig || !Array.isArray(tableData)) {
        return [];
      }

      const rows = tableData
        .filter((row) => row && row["channel"] && row[metric] != null)
        .map((row) => {
          const value = this.parse_prediction_metric_value(row[metric]);

          return {
            channel: row["channel"],
            value,
            tooltip: `${row["channel"]}<br/>${metricConfig.label}: ${this.format_prediction_metric_value(value)}`,
          };
        })
        .filter((row) => row.value > 0);

      return [
        ["Channel", metricConfig.label, "Tooltip"],
        ...rows.map((row) => [row.channel, row.value, row.tooltip]),
      ];
    },
    parse_prediction_metric_value: function (value) {
      if (typeof value === "number") {
        return value;
      }

      if (typeof value === "string") {
        const parsedValue = Number(value.replace(/[$,%\s,]/g, ""));
        return Number.isNaN(parsedValue) ? 0 : parsedValue;
      }

      return 0;
    },
    format_prediction_metric_value: function (value) {
      return value.toLocaleString("en-CA", {
        style: "currency",
        currency: "CAD",
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
        notation: "compact",
      });
    },
    generate_gchart_format: function (data) {
      let rows = [];
      let row_tmp = [];
      for (let row of data) {
        row_tmp = [];
        for (let val of row) {
          row_tmp.push({ f: null, v: val });
        }
        rows.push({ c: row_tmp });
      }
      return { cols: this.prediction_header, rows: rows };
    },
    updateLevelVal: function (indexShift) {
      let curIdx =
        this.available_level_vals.indexOf(this.selected_level_val) + indexShift;
      this.selected_level_val = this.available_level_vals[curIdx];
    },
    buildPrediction: async function () {
      this.update_data_loading(true);
      let token = await this.$auth.getTokenSilently();
      let payload = {
        project_id: this.$project.get_val(),
        project_plan_id: this.project_plan_id.toString(),
        model_version: this.model_version || "v1",
      };

      if (this.saved) {
        payload["pull_from_saved_opt"] = true;
      }
      if (this.version_tag) {
        payload["version_tag"] = this.version_tag;
      }

      let prediction_data = await api_caller
        .get_optimization_data(token, payload)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch(() => {
          return false;
        });
      if (prediction_data) {
        this.opt_data = prediction_data["summary_tbl"];
        this.opt_overview_data = [];
        this.headers = prediction_data["summary_tbl_header"];
        this.prediction_overview_data = prediction_data["overview"];

        this.optimizations = prediction_data["optimizations"];
        this.optimization_data = prediction_data["optimization_data"];

        this.update_data_loading(false);
        this.$emit("optimization_finished");
      } else {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        this.update_data_loading(false);
      }
    },
    empty_table_click: function () {},
    get_optimization_tab_key: function (optimization, index) {
      return `${index}-${optimization}`;
    },
    select_overview_tab: function () {
      this.my_optimization = "overview";
      this.selected_optimization_tab_key = "overview";
    },
    select_optimization_tab: function (optimization, index) {
      this.my_optimization = optimization;
      this.selected_optimization_tab_key = this.get_optimization_tab_key(
        optimization,
        index
      );
    },
    handle_row_click: function (value) {
      if (value["opt"] !== "Base") {
        const selectedIndex = this.opt_data.indexOf(value) - 1;
        if (selectedIndex >= 0) {
          this.select_optimization_tab(value["opt"], selectedIndex);
          return;
        }

        this.my_optimization = value["opt"];
        this.selected_optimization_tab_key = this.get_optimization_tab_key(
          value["opt"],
          this.optimizations.indexOf(value["opt"])
        );
      }
    },
    forward_alert: function (type, message) {
      this.$emit("triggerAlert", type, message);
    },
    exportPredictionPages: async function(plan_name) {
      const pptxBuilder = new PPTXBuilderPrediction(plan_name);
      let element, chart_data, chart_image;

      this.select_overview_tab();
      await DELAY(EXPORT_DELAY_IN_MS);
      
      // add overview table element
      element = {
        table_headers: this.optimization_header,
        table_data: this.opt_data,
      }
      pptxBuilder.add_element(element, 'Overview', 'table')

      // add overview bar chart element
      chart_data = echarts.getInstanceByDom(document.getElementById(this.chart_mapping['overview_chart']['chart_id']));
      chart_image = chart_data.getDataURL({
        type: 'svg', // You can change the image type here
        pixelRatio: 5, // Adjust the pixel ratio for higher resolution
      });

      element = {
        chart_name: this.chart_mapping['overview_chart']['chart_name'],
        chart_image: chart_image
      };

      pptxBuilder.add_element(element, 'Overview', 'chart');

      // export each optimization elements
      for (let idx = 0; idx < this.optimizations.length; idx++) {
        const opt = this.optimizations[idx];

        this.select_optimization_tab(opt, idx);
        await DELAY(EXPORT_DELAY_IN_MS);

        // add optimization table element
        element = {
          table_headers: this.optimization_data[opt]['tbl_header'],
          table_data: this.optimization_data[opt]['tbl_data'],
        }
        pptxBuilder.add_element(element, opt, 'table');

        // add optimization statistic element
        for (let stat of STAT_ELEMENTS) {
          element = {
            stats_name: stat.name,
            value: this.optimization_data[opt][stat.value],
            change: this.optimization_data[opt][stat.change],
          };
          pptxBuilder.add_element(element, opt, 'stats');
        }

        // add optimization pie chart elements
        for (let chartKey of ["prediction_spend_chart", "prediction_volume_chart"]) {
          chart_data = echarts.getInstanceByDom(
            document.getElementById(this.chart_mapping[chartKey]["chart_id"])
          );
          chart_image = chart_data.getDataURL({
            type: 'svg', // You can change the image type here
            pixelRatio: 5, // Adjust the pixel ratio for higher resolution
          });

          element = {
            chart_name: this.chart_mapping[chartKey]["chart_name"],
            chart_image: chart_image
          };

          pptxBuilder.add_element(element, opt, 'chart');
        }
      }
      pptxBuilder.build();
      pptxBuilder.save();

      this.select_overview_tab();
      await DELAY(EXPORT_DELAY_IN_MS);
    }
  },
};
</script>

<style>
.forecasting-prediction-container {
  max-width: 100%;
  height: 100%;
  overflow-y: auto;
}
.forecasting-prediction-loading {
  height: 100%;
  width: 100%;
}
.graph_select_title {
  line-height: 1.5em;
  font-family: "Poppins";
  font-weight: 600 !important;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  width: calc(80%);
  text-align: left;
  overflow: hidden;
}
.graph_select_options {
  line-height: 1em;
  font-family: "Poppins";
  font-weight: 400 !important;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  width: calc(100%);
  text-align: left;
  overflow: hidden;
}
.optimization_calculator_action {
  font-family: "Poppins" !important;
  font-weight: 400;
}
.prediction_title {
  font-family: "Poppins" !important;
  font-weight: 700;
}
.forecasting_prediction_layout {
  align-items: stretch;
  flex-wrap: wrap;
  min-height: calc(100vh - 150px);
  width: 100%;
}
.forecasting_prediction_tabs {
  display: flex;
  flex-direction: column;
  min-height: inherit;
  overflow-y: auto;
  width: 100%;
}
.forecasting_prediction_rail_label {
  color: #757575;
  display: block;
  font-family: "Poppins" !important;
  font-size: 0.72em;
  font-weight: 500;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
  text-transform: uppercase;
}
.forecasting_prediction_tab {
  align-items: center;
  background: #ffffff;
  border: 1px solid #9e9e9e;
  border-radius: 4px;
  color: #757575;
  cursor: pointer;
  display: flex;
  font-family: "Poppins" !important;
  font-size: 0.85em;
  font-weight: 500;
  justify-content: flex-start;
  letter-spacing: 0;
  margin: 0 0 8px 0;
  min-height: 48px;
  overflow: hidden;
  padding: 0 10px;
  text-align: left;
  text-overflow: ellipsis;
  text-transform: none;
  white-space: nowrap;
  width: 100%;
}
.forecasting_prediction_tab_active {
  border-color: #000000;
  color: #000000;
  font-weight: 600;
}
.forecasting_prediction_tab:hover {
  border-color: #000000;
}
.forecasting_prediction_divider {
  align-self: stretch;
  display: none;
  min-height: inherit;
}
.forecasting_prediction_divider .v-divider {
  min-height: inherit;
}
.forecasting_prediction_content {
  min-height: inherit;
  min-width: 0;
}
.forecasting_prediction_blocking_chart {
  width: 100%;
}
.forecasting_prediction_overview {
  align-content: flex-start;
  min-height: inherit;
}
.forecasting_prediction_pie_stack {
  align-content: flex-start;
  gap: 12px;
}
@media (min-width: 960px) {
  .forecasting_prediction_layout {
    flex-wrap: nowrap;
  }
  .forecasting_prediction_tabs {
    flex: 0 0 8.333333%;
    max-width: 8.333333%;
    width: 8.333333%;
  }
  .forecasting_prediction_divider {
    display: flex;
  }
}
</style>
